import { useEffect, useState } from "react";
import { PageHeader } from "../../components/common/PageHeader";
import { DataTable } from "../../components/common/DataTable";
import { FormModal } from "../../components/common/FormModal";
import { ConfirmDialog } from "../../components/common/ConfirmDialog";
import { TextField, SelectField } from "../../components/common/Form";
import { Button } from "@/components/ui/button";
import { StockService } from "../../services";
import { dateTime } from "../../utils/format";
import { Plus, Pencil, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useCan } from "../../hooks/useCan";

const adjustmentTypes = [
  "DAMAGE", "BREAKAGE", "EXPIRED", "MANUAL_CORRECTION", "COMPLIMENTARY", "CANCELLATION_RESTORE",
].map((v) => ({ value: v, label: v.replace("_", " ") }));

const empty = { batchSelection: "", adjustmentType: "DAMAGE", quantity: 0, reason: "" };

export const StockAdjustmentsPage = () => {
  const can = useCan();
  const [rows, setRows] = useState([]);
  const [batches, setBatches] = useState([]);
  const [restaurantBatches, setRestaurantBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([StockService.adjustments(), StockService.adjustableBatches()])
      .then(([a, availableBatches]) => {
        setRows(a);
        setBatches(availableBatches.filter((batch) => batch.stockSource === "BOTTLE"));
        setRestaurantBatches(availableBatches.filter((batch) => batch.stockSource === "RESTAURANT"));
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditingId(null); setForm(empty); setErrors({}); setOpen(true); };
  const openEdit = (r) => {
    setEditingId(r.adjustmentId);
    setForm({
      batchSelection: `${r.stockSource || "BOTTLE"}:${r.batchId ?? ""}`,
      adjustmentType: r.adjustmentType || "DAMAGE",
      quantity: r.quantity ?? 0,
      reason: r.reason || "",
    });
    setErrors({});
    setOpen(true);
  };

  const validate = () => {
    const e = {};
    if (!form.batchSelection) e.batchId = "Required";
    if (!form.adjustmentType) e.adjustmentType = "Required";
    if (!form.quantity || Number(form.quantity) === 0) e.quantity = "Cannot be 0";
    if (!form.reason) e.reason = "Required";
    setErrors(e); return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    setSaving(true);
    const [stockSource, batchId] = String(form.batchSelection).split(":");
    const payload = {
      stockSource, batchId: Number(batchId), adjustmentType: form.adjustmentType,
      quantity: Number(form.quantity), reason: form.reason,
    };
    try {
      if (editingId) { await StockService.updateAdjustment(editingId, payload); toast.success("Adjustment updated"); }
      else { await StockService.adjust(payload); toast.success("Stock adjusted"); }
      setOpen(false); setForm(empty); setEditingId(null); load();
    } catch (e) { toast.error(e?.response?.data?.message || "Failed"); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await StockService.removeAdjustment(confirmDelete.adjustmentId);
      toast.success("Adjustment reversed");
      setConfirmDelete(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.message || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader
        title="Stock Adjustments"
        subtitle="Damage, breakage, expired, manual correction, complimentary or cancellation restore."
        actions={can.create && <Button onClick={openCreate} className="bg-navy-800 hover:bg-navy-900 text-white" data-testid="adj-add"><Plus className="h-4 w-4 mr-2" /> New Adjustment</Button>}
      />
      <DataTable
        loading={loading}
        rows={rows.map((r) => ({ ...r, key: r.adjustmentId }))}
        testId="adj-table"
        searchable
        searchKeys={["stockSource", "bottleName", "biteName", "batchCode", "adjustmentType", "reason", "adjustedByFullName"]}
        searchPlaceholder="Search adjustments by batch, type or reason…"
        columns={[
          { key: "stockSource", header: "Source", render: (r) => r.stockSource === "RESTAURANT" ? "RESTAURANT" : "BAR / BOTTLE" },
          { key: "product", header: "Product", render: (r) => r.biteName || r.bottleName || "-" },
          { key: "batchCode", header: "Batch" },
          { key: "adjustmentType", header: "Type" },
          { key: "quantity", header: "Qty", render: (r) => <span className={r.quantity < 0 ? "text-rose-600" : "text-emerald-600"}>{r.quantity > 0 ? `+${r.quantity}` : r.quantity}</span> },
          { key: "reason", header: "Reason" },
          { key: "adjustedByFullName", header: "User" },
          { key: "createdAt", header: "When", render: (r) => dateTime(r.createdAt) },
          {
            key: "actions",
            header: "",
            render: (r) => can.modify ? (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => openEdit(r)} data-testid={`adj-edit-${r.adjustmentId}`} title="Edit"><Pencil className="h-3.5 w-3.5" /></Button>
                <Button variant="outline" size="sm" onClick={() => setConfirmDelete(r)} data-testid={`adj-delete-${r.adjustmentId}`} title="Reverse adjustment" className="text-rose-600 hover:bg-rose-50"><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            ) : <span className="text-slate-300 text-xs">—</span>,
          },
        ]}
      />
      <FormModal
        open={open}
        onOpenChange={setOpen}
        title={editingId ? "Edit adjustment" : "New stock adjustment"}
        onSubmit={submit}
        loading={saving}
        submitText={editingId ? "Save" : "Create"}
        description={editingId ? "Editing rolls back the previous quantity and applies the new one to the linked batch." : "Adjustments update the selected stock batch."}
      >
        <SelectField label="Product / batch" required value={form.batchSelection} onChange={(v) => setForm({ ...form, batchSelection: v })}
          options={[
            ...batches.map((b) => ({ value: `BOTTLE:${b.batchId}`, label: `BAR / BOTTLE · ${b.batchCode} · ${b.productName} · rem ${b.availableQuantity}` })),
            ...restaurantBatches.map((b) => ({ value: `RESTAURANT:${b.batchId}`, label: `RESTAURANT · ${b.productName} · ${b.batchCode} · qty ${b.availableQuantity}` })),
          ]}
          error={errors.batchId} testId="adj-batch" />
        <SelectField label="Adjustment type" required value={form.adjustmentType} onChange={(v) => setForm({ ...form, adjustmentType: v })}
          options={adjustmentTypes} error={errors.adjustmentType} testId="adj-type" />
        <TextField label="Quantity (negative reduces, positive increases)" required type="number" value={form.quantity}
          onChange={(v) => setForm({ ...form, quantity: v })} error={errors.quantity} testId="adj-qty" />
        {Number(form.quantity) < 0 && (
          <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded">
            <AlertTriangle className="h-4 w-4" /> Negative quantity will reduce remaining stock.
          </div>
        )}
        <TextField label="Reason" required value={form.reason} onChange={(v) => setForm({ ...form, reason: v })} error={errors.reason} testId="adj-reason" />
      </FormModal>
      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(v) => !v && setConfirmDelete(null)}
        title="Reverse adjustment?"
        description={confirmDelete ? `Adjustment on batch ${confirmDelete.batchCode} (${confirmDelete.quantity > 0 ? "+" : ""}${confirmDelete.quantity}) will be reversed and the linked batch's remaining stock will be restored.` : ""}
        confirmText="Reverse"
        danger
        loading={busy}
        onConfirm={remove}
      />
    </div>
  );
};
